import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { api, API_BASE } from '../../api/client';
import EditableField from '../../components/admin/EditableField';

interface Project {
  id: number;
  title: string;
  description: string;
}

interface Activity {
  id: number;
  title: string;
  description: string;
  projects: Project[];
}

export default function AdminActivitiesPage() {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [expanded, setExpanded] = useState<number | null>(null);

  const [newTitle, setNewTitle] = useState('');
  const [newDesc, setNewDesc] = useState('');
  const [newLang, setNewLang] = useState('ru');
  const [creating, setCreating] = useState(false);

  const [newProjectTitle, setNewProjectTitle] = useState('');
  const [newProjectDesc, setNewProjectDesc] = useState('');
  const [creatingProject, setCreatingProject] = useState<number | null>(null);

  const loadActivities = async () => {
    const titlesRes = await axios.get(`${API_BASE}/activities/get-all-titles`);
    const titles: string[] = titlesRes.data;

    const loaded: Activity[] = [];
    for (let i = 0; i < titles.length; i++) {
      try {
        const res = await axios.get(`${API_BASE}/activities/get-by-id/${i + 1}`);
        const projectsRes = await axios.get(`${API_BASE}/activities/get-all-activity-project-by-id/${i + 1}`);
        loaded.push({ ...res.data, projects: projectsRes.data });
      } catch {}
    }
    setActivities(loaded);
  };

  useEffect(() => { loadActivities(); }, []);

  const createActivity = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/activities/create', { title: newTitle, description: newDesc, language: newLang });
      setNewTitle('');
      setNewDesc('');
      setNewLang('ru');
      await loadActivities();
    } finally {
      setCreating(false);
    }
  };

  const updateActivity = async (id: number, field: 'title' | 'description', value: string) => {
    const activity = activities.find(a => a.id === id)!;
    await api.put(`/activities/upadte/${id}`, {
      title: field === 'title' ? value : activity.title,
      description: field === 'description' ? value : activity.description,
    });
    await loadActivities();
  };

  const createProject = async (e: React.FormEvent, activityId: number) => {
    e.preventDefault();
    setCreatingProject(activityId);
    try {
      await api.post('/activities/create-activity-project', {
        title: newProjectTitle,
        description: newProjectDesc,
        activityId,
      });
      setNewProjectTitle('');
      setNewProjectDesc('');
      await loadActivities();
    } finally {
      setCreatingProject(null);
    }
  };

  return (
    <div style={styles.page}>
      <h2 style={styles.pageTitle}>Управление деятельностью</h2>

      {/* ── Создать деятельность ── */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>➕ Создать деятельность</h3>
        <form onSubmit={createActivity} style={styles.form}>
          <input
            style={styles.input}
            placeholder="Название деятельности"
            value={newTitle}
            onChange={e => setNewTitle(e.target.value)}
            required
          />
          <textarea
            style={{ ...styles.input, resize: 'vertical' }}
            placeholder="Описание деятельности"
            rows={3}
            value={newDesc}
            onChange={e => setNewDesc(e.target.value)}
            required
          />
          <select style={styles.input} value={newLang} onChange={e => setNewLang(e.target.value)}>
            <option value="ru">🇷🇺 Русский</option>
            <option value="ky">🇰🇬 Кыргызский</option>
            <option value="en">🇬🇧 English</option>
          </select>
          <button style={styles.btnPrimary} type="submit" disabled={creating}>
            {creating ? 'Создание...' : 'Создать'}
          </button>
        </form>
      </div>

      {/* ── Список деятельностей ── */}
      {activities.map(activity => (
        <div key={activity.id} style={styles.card}>
          <div style={styles.activityHeader} onClick={() => setExpanded(expanded === activity.id ? null : activity.id)}>
            <span style={styles.activityTitle}>{activity.title}</span>
            <span style={{ fontSize: '0.8rem', color: '#888' }}>{expanded === activity.id ? '▲' : '▼'}</span>
          </div>

          {expanded === activity.id && (
            <div style={styles.activityBody}>
              {/* Название */}
              <div style={styles.fieldRow}>
                <span style={styles.fieldLabel}>Название:</span>
                <EditableField
                  value={activity.title}
                  onSave={v => updateActivity(activity.id, 'title', v)}
                />
              </div>

              {/* Описание */}
              <div style={styles.fieldRow}>
                <span style={styles.fieldLabel}>Описание:</span>
                <EditableField
                  value={activity.description}
                  onSave={v => updateActivity(activity.id, 'description', v)}
                  multiline
                />
              </div>

              {/* Проекты */}
              <div style={{ marginTop: 20 }}>
                <h4 style={styles.subTitle}>Проекты и результаты</h4>

                {activity.projects.length === 0 ? (
                  <p style={{ color: '#888', fontSize: '0.9rem' }}>Проектов пока нет</p>
                ) : (
                  activity.projects.map(p => (
                    <div key={p.id} style={styles.projectCard}>
                      <div style={styles.fieldRow}>
                        <span style={styles.fieldLabel}>Название:</span>
                        <span>{p.title}</span>
                      </div>
                      <div style={styles.fieldRow}>
                        <span style={styles.fieldLabel}>Описание:</span>
                        <span>{p.description}</span>
                      </div>
                    </div>
                  ))
                )}

                {/* Создать проект */}
                <form onSubmit={e => createProject(e, activity.id)} style={{ ...styles.form, marginTop: 16 }}>
                  <h5 style={{ color: '#1a3a6b', marginBottom: 4 }}>Добавить проект</h5>
                  <input
                    style={styles.input}
                    placeholder="Название проекта"
                    value={newProjectTitle}
                    onChange={e => setNewProjectTitle(e.target.value)}
                    required
                  />
                  <textarea
                    style={{ ...styles.input, resize: 'vertical' }}
                    placeholder="Описание / результаты"
                    rows={2}
                    value={newProjectDesc}
                    onChange={e => setNewProjectDesc(e.target.value)}
                    required
                  />
                  <button style={styles.btnSecondary} type="submit" disabled={creatingProject === activity.id}>
                    {creatingProject === activity.id ? 'Добавление...' : '➕ Добавить проект'}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '32px', maxWidth: 900, margin: '0 auto' },
  pageTitle: { color: '#1a3a6b', fontSize: '1.6rem', marginBottom: 24 },
  card: {
    background: '#fff',
    borderRadius: 10,
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    marginBottom: 16,
    overflow: 'hidden',
  },
  cardTitle: { color: '#1a3a6b', marginBottom: 16, fontSize: '1rem' },
  activityHeader: {
    padding: '16px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    cursor: 'pointer',
    background: '#f0f4fb',
    userSelect: 'none',
  },
  activityTitle: { fontWeight: 600, color: '#1a3a6b', fontSize: '1rem' },
  activityBody: { padding: '20px' },
  fieldRow: { display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14, flexWrap: 'wrap' },
  fieldLabel: { fontWeight: 600, color: '#555', fontSize: '0.9rem', minWidth: 90, paddingTop: 2 },
  subTitle: { color: '#1a3a6b', marginBottom: 12, fontSize: '0.95rem' },
  projectCard: {
    background: '#f8f9fa',
    border: '1px solid #e0e6f0',
    borderRadius: 8,
    padding: '12px 16px',
    marginBottom: 8,
  },
  form: { display: 'flex', flexDirection: 'column', gap: 10, padding: '0 20px 20px' },
  input: {
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid #dde3ef',
    fontSize: '0.95rem',
    outline: 'none',
  },
  btnPrimary: {
    padding: '10px 20px',
    background: '#1a3a6b',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    alignSelf: 'flex-start',
  },
  btnSecondary: {
    padding: '8px 16px',
    background: '#27ae60',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: '0.9rem',
    fontWeight: 600,
    cursor: 'pointer',
    alignSelf: 'flex-start',
  },
};
